import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { TenantAppDownloadsChartResponseDto } from '@/generated/korza-api-client/types.gen';
import { type ChartGranularity } from '@/lib/chart-axis';
import { bucketSumChartData } from '@/lib/hooks/use-chart-data';
import { useChartTooltip } from '@/lib/hooks/use-chart-tooltip';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import type { ChartRange } from '@/lib/partial-buckets';
import { MetricsBarChart } from '@/components/ui/charts/bar-chart';
import { MetricsChartBarSeries } from '@/components/ui/charts/chart-bar-series';

type TenantAppDownloadsChartPointDto =
  TenantAppDownloadsChartResponseDto['points'][number];

export type Granularity = ChartGranularity;

interface DownloadsChartProps {
  granularity: Granularity;
  points: TenantAppDownloadsChartPointDto[];
  /** The filter's period: hatches the partial first/last bucket (CHART-01). */
  range?: ChartRange;
  syncId?: string;
  syncMethod?: 'index' | 'value';
}

/** Downloads per bucket, as bars (ANLT-06). */
export function DownloadsChart({
  granularity,
  points,
  range,
  syncId,
  syncMethod,
}: DownloadsChartProps) {
  const { LL, locale } = useI18nContext();

  const rows = useMemo(
    () =>
      bucketSumChartData(
        points,
        granularity,
        locale,
        (p) => p.downloadsCount,
        'downloads'
      ),
    [granularity, locale, points]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const chartData = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        partial: partial.indexes.includes(index),
      })),
    [rows, partial.indexes]
  );

  const tooltipContent = useChartTooltip({
    formatRows: (_label, payload) => {
      const total = (payload ?? []).reduce(
        (sum, entry) => sum + ((entry.value as number) ?? 0),
        0
      );
      return [
        {
          label: LL.metricsPage.downloads.title(),
          value: new Intl.NumberFormat(locale).format(total),
        },
      ];
    },
  });

  return (
    <MetricsBarChart
      data={chartData}
      partialLegend={partial.edges}
      syncId={syncId}
      syncMethod={syncMethod}
      tooltipContent={tooltipContent}
    >
      <MetricsChartBarSeries
        dataKey="downloads"
        name={LL.metricsPage.downloads.title()}
        partialIndexes={partial.indexes}
        partialLabels={partial.labels}
      />
    </MetricsBarChart>
  );
}
